import React, { useState, useEffect, useRef } from "react";
import { useConsent } from "../context/ConsentContext";
import { GoogleMap, Marker, InfoWindow } from "@react-google-maps/api";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer
} from "recharts";
import { useTranslation } from "react-i18next";
import { getAuth } from "firebase/auth";
import axios from "../utils/axios";
import { onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import MeasureApp from "../pages/MeasureApp"; 
import {ProductsManagement} from "../components/ProductsManagement"; 
import { useAccess } from "../context/AccessContext";
import { hasModule, hasPermission } from "../utils/accessHelpers";



const Dashboard = ({ isLoaded }) => {
  const mapRef = useRef(null);
  const clustererRef = useRef(null);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const subjectClustererRef = useRef(null);
  const [markerView, setMarkerView] = useState("both");
  
  const { consent, decision, categories } = useConsent();

  const { access, loading: accessLoading } = useAccess();

  const canSeeHR = hasModule(access, "hr");
  const canSeeCRM = hasModule(access, "crm");
  const canSeeERP = hasModule(access, "erp") || hasPermission(access, "erp.access");
  const canSeeProjects = hasModule(access, "projects");
  const canSeeProducts = hasModule(access, "products");
  const canSeeBank = hasModule(access, "bank");
  const canSeeFinance = hasModule(access, "finance");
  const canSeeLogistics = hasModule(access, "logistics");
  const canSeeProcurement = hasModule(access, "procurement");
  const canSeeMail = hasModule(access, "mail");
  const canSeeDashboard = hasModule(access, "dashboard");
  const canSeeIt = hasModule(access, "it");
  const canSeeMap = hasModule(access, "map");
  const showProjectsMapSection = canSeeProjects || canSeeProcurement;
  const showProjectsTableSection = canSeeProjects;

  const canWriteHR = hasPermission(access, "hr.people.write");
  const canWriteCRM = hasPermission(access, "crm.write");
  const canWriteProjects = hasPermission(access, "projects.write");
  const canAssignTasks = hasPermission(access, "projects.tasks.assign");
  const canReadAllProjects = hasPermission(access, "projects.read.all");




  const canProjectAdmin = canWriteProjects || canAssignTasks;
  const canSeeProjectUserColumn = canSeeProjects && canProjectAdmin;
  const canSeeProjectStatusColumn = canSeeProjects && canProjectAdmin;
  const canManageProjectActivation = canProjectAdmin;
  const canDeleteProjects = canProjectAdmin; // kasnije možeš poseban permission
  const canSeeSubjectMarkers = canSeeCRM || canSeeMap;
  const canUseMarkerSwitcher = canSeeProjects && canSeeSubjectMarkers;
  const canSeeUserActivity = canSeeDashboard;


  const [projects] = useState([
    { id: 1, name: "Modular Objects HQ", lat: 41.13684820959852, lng: -104.8029678981709 },
  ]);
  
  const { t } = useTranslation();
  const [isDark, setIsDark] = useState(document.body.classList.contains("dark"));
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [user, setUser] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [userProjects, setUserProjects] = useState([]);
  const [userLocationProjects, setUserLocationProjects] = useState([]);

  const [searchName, setSearchName] = useState("");
  const [searchLocation, setSearchLocation] = useState("");
  const [searchEmail, setSearchEmail] = useState("");

  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [projectIdToDeactivate, setProjectIdToDeactivate] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  // ✅ Detekcija WebXR AR podrške
  const [arCapable, setArCapable] = useState(null); // null=nepoznato, true/false=poznato

  const [arSupported, setArSupported] = useState(false);
  const [showMeasure, setShowMeasure] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [projectIdToDelete, setProjectIdToDelete] = useState(null);





useEffect(() => {
}, [consent, isLoaded]);

useEffect(() => {
  let cancelled = false;
  async function checkAR() {
    if (typeof navigator === "undefined" || !("xr" in navigator)) {
      if (!cancelled) setArSupported(false);
      return;
    }
    try {
      const ok = await navigator.xr.isSessionSupported("immersive-ar");
      if (!cancelled) setArSupported(!!ok);
    } catch {
      if (!cancelled) setArSupported(false);
    }
  }
  checkAR();
  return () => { cancelled = true; };
}, []);

useEffect(() => {
  let cancelled = false;

  async function checkAR() {
    // WebXR zahteva secure context (https ili localhost)
    if (!window.isSecureContext) {
      if (!cancelled) setArCapable(false);
      return;
    }
    if (!('xr' in navigator)) {
      if (!cancelled) setArCapable(false);
      return;
    }
    try {
      const supported = await navigator.xr.isSessionSupported('immersive-ar');
      if (!cancelled) setArCapable(!!supported);
    } catch {
      if (!cancelled) setArCapable(false);
    }
  }

  checkAR();
  return () => { cancelled = true; };
}, []);


useEffect(() => {
  const fetchSubjects = async () => {
    try {
      const res = await axios.get("https://modularobjects.com/checkout/api/subjects/locations");
      setSubjects(res.data);
    } catch (err) {
      console.error("Greška pri preuzimanju subjekata:", err);
    }
  };

  fetchSubjects();
}, []);




const fetchUserProjects = async (uid, token, canLoadAllProjects) => {
  try {
    const config = { headers: { Authorization: `Bearer ${token}` } };

    const endpoint = canLoadAllProjects
      ? "https://modularobjects.com/checkout/api/project/all"
      : `https://modularobjects.com/checkout/api/project/user/${uid}`;

    const res = await axios.get(endpoint, config);

    const projects = res.data.map((p) => ({
      ...p,
      status: String(p.status || "").trim().toLowerCase(),
      location_lat: parseFloat(p.location_lat),
      location_lng: parseFloat(p.location_lng),
    }));

    setUserProjects(projects);
    return projects;
  } catch (err) {
    console.error("Greška pri dohvatanju projekata:", {
      status: err?.response?.status,
      url: err?.config?.url,
      data: err?.response?.data,
    });
    return [];
  }
};





const normalizeStatus = (status) => String(status || "").trim().toLowerCase();

const filteredProjects = userProjects.filter((p) =>
  (p.project_name || "").toLowerCase().includes(searchName.toLowerCase()) &&
  (p.location || "").toLowerCase().includes(searchLocation.toLowerCase()) &&
  (p.user_email || "").toLowerCase().includes(searchEmail.toLowerCase())
);

// Funkcija za otvaranje modala
const openDeactivateModal = (projectId) => {
  setProjectIdToDeactivate(projectId);
  setShowDeactivateModal(true);
};

// Funkcija za zatvaranje modala
const closeDeactivateModal = () => {
  setShowDeactivateModal(false);
  setProjectIdToDeactivate(null);
};

const handleDeactivateProject = async () => {
  console.log("Deactivating project with ID:", projectIdToDeactivate);

  try {
    if (!firebaseUser) {
      alert("User is not authenticated");
      return;
    }

    const token = await firebaseUser.getIdToken();
    const config = { headers: { Authorization: `Bearer ${token}` } };

    const url = `https://modularobjects.com/checkout/api/project/deactivate/${projectIdToDeactivate}`;
    console.log("URL for deactivation:", url);

    const response = await axios.put(url, {}, config);
    console.log("Response data:", response.data);

    if (response.data.success) {
      await fetchUserProjects(firebaseUser.uid, token, canReadAllProjects);
    }
  } catch (err) {
    console.error("Error during project deactivation:", err.response ? err.response.data : err);
    alert("Failed to deactivate project");
  } finally {
    closeDeactivateModal();
  }
};

const handleActivateProject = async (projectId) => {
  try {
    if (!firebaseUser) {
      alert("User is not authenticated");
      return;
    }

    const token = await firebaseUser.getIdToken();
    const config = {
      headers: { Authorization: `Bearer ${token}` }
    };

    const url = `https://modularobjects.com/checkout/api/project/activate/${projectId}`;
    const response = await axios.put(url, {}, config);

    if (response.data.success) {
      await fetchUserProjects(firebaseUser.uid, token, canReadAllProjects);
    }
  } catch (err) {
    console.error("Error activating project:", err.response ? err.response.data : err);
    alert("Failed to activate project");
  }
};

useEffect(() => {
  const auth = getAuth();

  const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
    if (!firebaseUser) {
      setFirebaseUser(null);
      setUser(null);
      setRoles([]);
      setUserProjects([]);
      return;
    }

    setFirebaseUser(firebaseUser);

    try {
      const token = await firebaseUser.getIdToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const res = await axios.get("https://modularobjects.com/checkout/api/user/me", config);
      const serverUser = res.data;

      setUser({
        ...serverUser,
        firebaseUid: firebaseUser.uid,
      });

      setRoles(serverUser.roles || []);
    } catch (err) {
      console.error("Greška pri dohvatanju korisnika:", err);
    }
  });

  return () => unsubscribe();
}, []);


useEffect(() => {
  const loadProjects = async () => {
    if (!firebaseUser || accessLoading) return;

    try {
      const token = await firebaseUser.getIdToken();
      await fetchUserProjects(firebaseUser.uid, token, canReadAllProjects);
    } catch (err) {
      console.error("Greška pri učitavanju projekata:", err);
    }
  };

  loadProjects();
}, [firebaseUser, accessLoading, canReadAllProjects]);
 


  const markerIcon = {
    path: window.google?.maps.SymbolPath.CIRCLE,
    fillColor: "#d6c176",
    fillOpacity: 1,
    strokeWeight: 1,
    strokeColor: "#ff0000",
    scale: 7,
  };

  const openDeleteModal = (projectId) => {
  setProjectIdToDelete(projectId);
  setShowDeleteModal(true);
};

const closeDeleteModal = () => {
  setShowDeleteModal(false);
  setProjectIdToDelete(null);
};


const handleDeleteProject = async () => {
  try {
    if (!firebaseUser) {
      alert("User is not authenticated");
      return;
    }

    const token = await firebaseUser.getIdToken();
    const config = {
      headers: { Authorization: `Bearer ${token}` }
    };

    const url = `https://modularobjects.com/checkout/api/project/${projectIdToDelete}`;
    const response = await axios.delete(url, config);

    if (response.data.success) {
      await fetchUserProjects(firebaseUser.uid, token, canLoadAllProjects);
    }
  } catch (err) {
  console.error("Error deleting project:");
  console.error("status:", err.response?.status);
  console.error("data:", err.response?.data);
  console.error("full:", err);
  alert(err.response?.data?.error || "Failed to delete project");
} finally {
  closeDeleteModal();
}
};
  

useEffect(() => {
  const observer = new MutationObserver(() => {
    setIsDark(document.body.classList.contains("dark"));
  });

  observer.observe(document.body, {
    attributes: true,
    attributeFilter: ["class"],
  });

  return () => observer.disconnect();
}, []);

  const mapStyles = {
    dark: [
      { featureType: "water", elementType: "geometry", stylers: [{ color: "#d6c176" }] },
      { featureType: "landscape", elementType: "geometry", stylers: [{ color: "#1e1e1e" }] },
      { featureType: "landscape.man_made", elementType: "geometry", stylers: [{ color: "#2a2a2a" }] },
      { featureType: "landscape.natural", elementType: "geometry", stylers: [{ color: "#2c2c2c" }] },
      { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#2b2b2b" }] },
      { featureType: "poi.attraction", elementType: "geometry", stylers: [{ color: "#2d2d2d" }] },
      { featureType: "road", elementType: "geometry", stylers: [{ color: "#444444" }] },
      { featureType: "poi", elementType: "geometry", stylers: [{ color: "#2a2a2a" }] },
      { featureType: "transit", elementType: "geometry", stylers: [{ color: "#2f2f2f" }] },
      { featureType: "administrative", elementType: "labels.text.fill", stylers: [{ color: "#aaaaaa" }] },
    ],
    light: [
      { featureType: "water", elementType: "geometry", stylers: [{ color: "#d6c176" }] },
      { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
      { featureType: "landscape", elementType: "geometry", stylers: [{ color: "#f0f0f0" }] },
      { featureType: "poi", elementType: "geometry", stylers: [{ color: "#e0e0e0" }] },
      { featureType: "transit", elementType: "geometry", stylers: [{ color: "#cccccc" }] },
      { elementType: "labels.text.fill", stylers: [{ color: "#333333" }] }
    ],
  };

  

const apps = [
  {
    href: "#",
    icon: "➕",
    title: t("dashboardkey.apps.newProject") || "Create New Project",
    desc:
      t("dashboardkey.apps.newProjectDesc") ||
      "Start from scratch and define a new project",
    hidden: !canSeeProjects || !canWriteProjects,
    onClick: () => {
      localStorage.removeItem("projectDefinitionFormData");
      navigate("/project/new/overview");
    },
  },
  {
  href: "/interior-editor",
  icon: "🛋️",
  title: t("dashboardkey.apps.interior_editor") || "Interior Editor",
  desc:
    t("dashboardkey.apps.interior_editor_desc") ||
    "Parametric modular interior editor for kitchens, wardrobes and furniture systems",
  // hidden:
  //   !hasModule(access, "interior_editor") &&
  //   !hasPermission(access, "interior.read"),
},
  {
    href: "/viewer",
    icon: "🧱",
    title: t("dashboardkey.apps.viewer_3d"),
    desc: t("dashboardkey.apps.viewer_3d_desc"),
    hidden: false,
  },
  {
    href: "/upload",
    icon: "💡",
    title: t("dashboardkey.apps.upload_idea"),
    desc: t("dashboardkey.apps.upload_idea_desc"),
    hidden: false,
  },

  {
    href: "/bi",
    icon: "📊",
    title: "BI Dashboard",
    desc: "Events / Sessions / Users + charts",
    hidden: !canSeeDashboard,
  },
  {
    href: "/mailbox",
    icon: "📧",
    title: t("dashboardkey.apps.mail") || "Mail App",
    desc:
      t("dashboardkey.apps.mail_desc") ||
      "Open mailbox, read conversations and manage emails",
    hidden: !canSeeMail,
  },
  {
    href: "/finance/bank",
    icon: "🏦",
    title: t("dashboardkey.apps.bank") || "Bank",
    desc:
      t("dashboardkey.apps.bank_desc") ||
      "Statements, transactions and cashflow",
    hidden: !canSeeBank,
  },
  {
  href: "/erp",
  icon: "📦",
  title: t("dashboardkey.apps.erp") || "ERP",
  desc:
    t("dashboardkey.apps.erp_desc") ||
    "Enterprise Resource Planning: documents, inventory, assets and logistics",
  hidden: !canSeeERP,
},
  {
    href: "/subjects",
    icon: "🏢",
    title: t("dashboardkey.apps.subjects"),
    desc: t("dashboardkey.apps.subjects_desc"),
    hidden: !canSeeCRM,
  },
  {
    href: "/technologies",
    icon: "🛠️",
    title: t("dashboardkey.apps.technologies"),
    desc: t("dashboardkey.apps.technologies_desc"),
    hidden: !canSeeProducts,
  },
  {
    href: "/subject-technologies",
    icon: "🔗",
    title: t("dashboardkey.apps.subject_technologies"),
    desc: t("dashboardkey.apps.subject_technologies_desc"),
    hidden: !canSeeProducts,
  },
  {
    href: "/machines",
    icon: "⚙️",
    title: t("dashboardkey.apps.machines"),
    desc: t("dashboardkey.apps.machines_desc"),
    hidden: !canSeeProducts,
  },
  {
    href: "/products",
    icon: "🛍️",
    title: t("dashboardkey.apps.products") || "Products & Materials",
    desc:
      t("dashboardkey.apps.products_desc") ||
      "Manage products, materials and inventory",
    hidden: !canSeeProducts,
  },
  {
    href: "/qrlocation",
    icon: "📈",
    title: t("dashboardkey.apps.map_module") || "MAP",
    desc:
      t("dashboardkey.apps.map_module_desc") ||
      "Kampanje, QR kodovi, lokacije",
    hidden: !canSeeMap,
  },
  {
  href: "/qaqc",
  icon: "🧪",
  title: t("dashboardkey.apps.project_delivery") || "QA/QC",
  desc:
    t("dashboardkey.apps.project_delivery_desc") ||
    "Verify delivery, quantities and quality of items through scanning, matching and discrepancy tracking",
  hidden: !(canSeeProjects || canSeeLogistics),
},
  {
    href: "/crm",
    icon: "👥",
    title: t("dashboardkey.apps.crm") || "CRM",
    desc:
      t("dashboardkey.apps.crm_desc") ||
      "Subjekti, aktivnosti, leadovi i pipeline",
    hidden: !canSeeCRM,
  },
  {
    href: "/hr",
    icon: "🧑‍💼",
    title: t("dashboardkey.apps.hr") || "HR",
    desc:
      t("dashboardkey.apps.hr_desc") ||
      "Employees, collaborators, organization and HR records",
    hidden: !canSeeHR,
  },
  {
    href: "/it",
    icon: "🖥️",
    title: "IT Admin",
    desc: "Access control, policies, scopes and system administration",
    hidden: !canSeeIt, // kasnije možeš da uvedeš poseban "it" modul
  },
  {
  href: "/it/notifications",
  icon: "🔔",
  title: "Notification Control",
  desc: "Analytics, escalations, push delivery and notification health",
  hidden: !canSeeIt,
},
  {
    href: "/ocr",
    icon: "🔎",
    title: t("dashboardkey.apps.ocr"),
    desc: t("dashboardkey.apps.ocr_desc"),
    hidden: !canSeeProducts,
  },
  ...(arSupported
    ? [
        {
          href: "#",
          icon: "📐",
          title: t("dashboardkey.apps.xr_measure") || "XR Measure",
          desc:
            t("dashboardkey.apps.xr_measure_desc") ||
            "Merenje i analiza prostora u AR okruženju",
          hidden: !canSeeProjects,
          onClick: () => setShowMeasure(true),
        },
      ]
    : []),
].filter((app) => !app.hidden);



  // Inicijalizacija mape i clusterera
  const handleMapLoad = (map) => {
    mapRef.current = map;
    updateMapMarkers();
  };







  // Ažuriranje markera i clusterera
  const updateMapMarkers = () => {
    if (markerView !== "projects" && markerView !== "both") {
  if (clustererRef.current) clustererRef.current.clearMarkers();
  return;
}
    if (!mapRef.current) return;

    const validProjects = userProjects.filter(p => 
  normalizeStatus(p.status) === "active" &&
  p.location_lat && p.location_lng &&
  !isNaN(p.location_lat) && 
  !isNaN(p.location_lng)
);

    // Zumiranje na projekte
    if (validProjects.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      validProjects.forEach(p => {
        bounds.extend({
          lat: p.location_lat,
          lng: p.location_lng
        });
      });
      mapRef.current.fitBounds(bounds, { padding: 50 });
    }

    // Kreiranje markera
    const markers = validProjects.map((p) => {
  const marker = new window.google.maps.Marker({
    position: { lat: p.location_lat, lng: p.location_lng },
    icon: markerIcon,
  });

  marker.addListener("click", () => {
    setSelectedProject(p);
  });

  return marker;
});

if (!clustererRef.current) {
  clustererRef.current = new MarkerClusterer({
    map: mapRef.current,
    markers,
    renderer: {
      render: ({ count, position }) => {
        const size = Math.min(10 + Math.sqrt(count) * 5, 30);
        return new window.google.maps.Marker({
          position,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            fillColor: "#d6c176",
            fillOpacity: 0.8,
            strokeColor: "#b89f50",
            strokeWeight: 2,
            scale: size,
          },
          label: {
            text: String(count),
            color: "#ffffff",
            fontSize: "12px",
            fontWeight: "bold",
          },
        });
      },
    },
  });




  // ✅ Rukovanje klikom na cluster
  clustererRef.current.addListener("clusterclick", (cluster) => {
  const map = mapRef.current;
  const bounds = new window.google.maps.LatLngBounds();

  const markers = cluster.getMarkers?.() || []; // fallback ako je `undefined`

  markers.forEach((marker) => {
    bounds.extend(marker.getPosition());
  });

  map.fitBounds(bounds);
  if (map.getZoom() > 15) {
    map.setZoom(map.getZoom() + 1);
  }
});


} else {
  clustererRef.current.clearMarkers();
  clustererRef.current.addMarkers(markers);
}

    // Inicijalizacija ili ažuriranje clusterera
    if (window.MarkerClusterer) {
      if (!clustererRef.current) {
        clustererRef.current = new MarkerClusterer({
          map: mapRef.current,
          markers,
          renderer: {
            render: ({ count, position }) => {
              const size = Math.min(10 + Math.sqrt(count) * 5, 30);
              return new window.google.maps.Marker({
                position,
                icon: {
                  path: window.google.maps.SymbolPath.CIRCLE,
                  fillColor: "#d6c176",
                  fillOpacity: 0.8,
                  strokeColor: "#b89f50",
                  strokeWeight: 2,
                  scale: size,
                },
                label: {
                  text: String(count),
                  color: "#ffffff",
                  fontSize: "12px",
                  fontWeight: "bold",
                },
              });
            },
          },
        });

        // Handler za klik na cluster
        clustererRef.current.addListener("clusterclick", (cluster) => {
          const map = mapRef.current;
          const bounds = new window.google.maps.LatLngBounds();
          
          cluster.getMarkers().forEach(marker => {
            bounds.extend(marker.getPosition());
          });
          
          // Zumiranje na cluster sa animacijom
          map.fitBounds(bounds, { 
            padding: 50,
            callback: () => {
              if (map.getZoom() > 15) return;
              map.setZoom(map.getZoom() + 1);
            }
          });
        });
      } else {
        clustererRef.current.clearMarkers();
        clustererRef.current.addMarkers(markers);
      }
    }
  };

useEffect(() => {
  if (isLoaded) {
    updateMapMarkers();
    if (canSeeSubjectMarkers) updateSubjectMarkers();
  }
}, [markerView]);


const updateSubjectMarkers = () => {
  if (markerView !== "subjects" && markerView !== "both") {
  if (subjectClustererRef.current) subjectClustererRef.current.clearMarkers();
  return;
}

  if (!mapRef.current || !subjects.length) return;

  const validSubjects = subjects.filter(s =>
    s.lat && s.lng && !isNaN(s.lat) && !isNaN(s.lng)
  );

  const subjectMarkers = validSubjects.map(subject => {
    const marker = new window.google.maps.Marker({
      position: { lat: parseFloat(subject.lat), lng: parseFloat(subject.lng) },
      icon: "http://maps.google.com/mapfiles/ms/icons/blue-dot.png",
    });

    marker.addListener("click", () => {
      setSelectedSubject(subject);
    });

    return marker;
  });

  if (!subjectClustererRef.current) {
    subjectClustererRef.current = new MarkerClusterer({
      map: mapRef.current,
      markers: subjectMarkers,
      renderer: {
        render: ({ count, position }) => {
          const size = Math.min(10 + Math.sqrt(count) * 5, 30);
          return new window.google.maps.Marker({
            position,
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              fillColor: "#4A90E2", // plava boja
              fillOpacity: 0.8,
              strokeColor: "#1F5EA8",
              strokeWeight: 2,
              scale: size,
            },
            label: {
              text: String(count),
              color: "#ffffff",
              fontSize: "12px",
              fontWeight: "bold",
            },
          });
        },
      },
    });

    subjectClustererRef.current.addListener("clusterclick", (cluster) => {
      const map = mapRef.current;
      const bounds = new window.google.maps.LatLngBounds();

      cluster.getMarkers().forEach(marker => {
        bounds.extend(marker.getPosition());
      });

      map.fitBounds(bounds);
      if (map.getZoom() > 15) {
        map.setZoom(map.getZoom() + 1);
      }
    });
  } else {
    subjectClustererRef.current.clearMarkers();
    subjectClustererRef.current.addMarkers(subjectMarkers);
  }
};

useEffect(() => {
  if (isLoaded && canSeeSubjectMarkers && subjects.length > 0) {
    updateSubjectMarkers();
  }
}, [subjects, isLoaded, canSeeSubjectMarkers]);



  // Ažuriranje markera kada se promene projekti
  useEffect(() => {
    if (isLoaded && userProjects.length > 0) {
      updateMapMarkers();
    }
  }, [userProjects, isLoaded]);

  useEffect(() => {
  if (isLoaded && canSeeSubjectMarkers  && subjects.length > 0) {
    updateSubjectMarkers(); // pozivaš funkciju
  }
}, [isLoaded, canSeeSubjectMarkers , subjects]);



  return (
   
    <div className="p-4 space-y-10">
      {showProjectsMapSection && (
  <>
    <h2 className="text-3xl font-bold text-primary-light dark:text-primary-dark mb-4">
      📍 {t("dashboardkey.project_locations")}
    </h2>

    {decision && isLoaded ? (
      <div className="h-[400px] w-full border rounded shadow-md border-gray-200 dark:border-neutral-700 bg-background-light dark:bg-neutral-900">
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "100%" }}
          zoom={6}
          options={{
            styles: isDark ? mapStyles.dark : mapStyles.light,
            gestureHandling: "greedy",
          }}
          onLoad={handleMapLoad}
        >
 
{canUseMarkerSwitcher  && (
  <div className="absolute bottom-8 left-2 z-10 bg-white rounded-sm shadow p-1 flex flex-col gap-1">
    <button
      onClick={() => setMarkerView("both")}
      className={`px-3 py-1 rounded text-sm font-medium ${
        markerView === "both" ? "bg-primary-light text-white" : "bg-white hover:bg-gray-100"
      }`}
    >
      All
    </button>
    <button
      onClick={() => setMarkerView("projects")}
      className={`px-3 py-1 rounded text-sm font-medium ${
        markerView === "projects" ? "bg-primary-light text-white" : "bg-white hover:bg-gray-100"
      }`}
    >
      Projects
    </button>
    <button
      onClick={() => setMarkerView("subjects")}
      className={`px-3 py-1 rounded text-sm font-medium ${
        markerView === "subjects" ? "bg-primary-light text-white" : "bg-white hover:bg-gray-100"
      }`}
    >
      Subjects
    </button>

  </div>
)}


      

  {/* 🧩 Marker i InfoWindow za projekat (kada se klikne) */}
  {selectedProject && (
    <InfoWindow
      position={{
        lat: selectedProject.location_lat,
        lng: selectedProject.location_lng,
      }}
      onCloseClick={() => setSelectedProject(null)}
    >
      <div className="p-2 bg-white dark:bg-neutral-800 rounded shadow">
        <h3 className="font-semibold text-primary-dark dark:text-primary-light">
          {selectedProject.project_name}
        </h3>
        <p className="text-sm text-gray-700 dark:text-gray-300">
          {selectedProject.location}
        </p>
        {canSeeProjectUserColumn  && selectedProject.user_email && (
          <p className="text-sm text-gray-700 dark:text-gray-300">
            👤 {selectedProject.user_email}
          </p>
        )}
        <button
          onClick={() => {
  navigate(`/project/${selectedProject.id}/overview`);
}}
          className="mt-2 text-sm text-black dark:text-white bg-primary px-2 py-1 rounded hover:bg-primary-dark"
        >
          🔍 Open Project
        </button>
      </div>
    </InfoWindow>
  )}

  {/* 🧭 InfoWindow za subjekt */}
  {selectedSubject && (
    <InfoWindow
      position={{
        lat: parseFloat(selectedSubject.lat),
        lng: parseFloat(selectedSubject.lng),
      }}
      onCloseClick={() => setSelectedSubject(null)}
    >
      <div className="p-2 bg-white dark:bg-neutral-800 rounded shadow max-w-xs">
        <h3 className="font-semibold text-blue-700 dark:text-blue-300">
          🏢 {selectedSubject.company_name}
        </h3>
        {selectedSubject.address && (
          <p className="text-sm text-gray-700 dark:text-gray-300">
            📍 {selectedSubject.address}
          </p>
        )}
        {selectedSubject.email && (
          <p className="text-sm text-gray-700 dark:text-gray-300">
            ✉️ {selectedSubject.email}
          </p>
        )}
        {selectedSubject.phone && (
          <p className="text-sm text-gray-700 dark:text-gray-300">
            ☎️ {selectedSubject.phone}
          </p>
        )}
      </div>
    </InfoWindow>
  )}
</GoogleMap>
      </div>
    ) : (
      <p className="text-sm italic text-gray-500">
        {t("cookie.declined_notice") || "Please accept cookies to load the map."}
      </p>
    )}
  </>
)}
      
    
      <div>
        <h3 className="text-3xl font-bold text-primary-light dark:text-primary-dark mb-4">📦 {t("dashboardkey.app_selection")}</h3>
        <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {apps.map((app) => (
  <div
    key={app.title}
    onClick={() => {
      if (app.onClick) {
        app.onClick(); // poziva custom logiku kao što je za novi projekat
      } else if (app.href) {
        navigate(app.href); // inače koristi samo navigate
      }
    }}
    className="cursor-pointer border p-4 rounded shadow-md border-gray-200 dark:border-neutral-700 bg-background-light dark:bg-neutral-900 transition hover:shadow-lg"
  >
    <h3 className="text-lg font-semibold text-primary-light">
      {app.icon} {app.title}
    </h3>
    <p className="text-sm text-text-light dark:text-text-dark">{app.desc}</p>
  </div>
))}
        </div>
        
        <div className="mt-10">
  <h3 className="text-2xl font-bold text-primary-light dark:text-primary-dark mb-4">
    📋 {t("dashboardkey.project_list") || "Project List"}
  </h3>
   
  {/* Filter Form */}
      <div className="flex flex-wrap gap-4 mb-4">
        <input
          type="text"
          placeholder="🔎 Name"
          value={searchName}
          onChange={(e) => setSearchName(e.target.value)}
          className="px-2 py-1 border rounded dark:bg-neutral-800 dark:text-white"
        />
        <input
          type="text"
          placeholder="📍 Location"
          value={searchLocation}
          onChange={(e) => setSearchLocation(e.target.value)}
          className="px-2 py-1 border rounded dark:bg-neutral-800 dark:text-white"
        />
        <input
          type="text"
          placeholder="👤 User Email"
          value={searchEmail}
          onChange={(e) => setSearchEmail(e.target.value)}
          className="px-2 py-1 border rounded dark:bg-neutral-800 dark:text-white"
        />
      </div>

  {/* Tabela sa scroll-om */}
  <div className="overflow-x-auto max-h-[400px] overflow-y-auto border border-gray-200 dark:border-gray-700 rounded shadow">
        <table className="min-w-full table-auto border-collapse">
  <thead className="bg-gray-100 dark:bg-neutral-800 sticky top-0 z-10">
    <tr>
      <th className="px-4 py-2 border">Name</th>
      <th className="px-4 py-2 border">Location</th>
      <th className="px-4 py-2 border">Lat / Lng</th>
      {canSeeProjectUserColumn  && <th className="px-4 py-2 border">User</th>}
      {canSeeProjectStatusColumn  && <th className="px-4 py-2 border">Status</th>}
      <th className="px-4 py-2 border">Action</th>
      <th className="px-4 py-2 border">
  {canManageProjectActivation  ? "Activate/Deactivate" : "Deactivate"}</th>
  {canDeleteProjects  && <th className="px-4 py-2 border">Delete</th>}
    </tr>
  </thead>
  <tbody>
    {filteredProjects.map((project) => (
      <tr key={project.id} className="hover:bg-gray-50 dark:hover:bg-neutral-800">
        <td className="px-4 py-2 border">{project.project_name}</td>
        <td className="px-4 py-2 border">{project.location}</td>
        <td className="px-4 py-2 border">
          {project.location_lat?.toFixed(4)}, {project.location_lng?.toFixed(4)}
        </td>
        {canSeeProjectUserColumn  && <td className="px-4 py-2 border">{project.user_email || "N/A"}</td>}
        {canSeeProjectStatusColumn  && <td className="px-4 py-2 border">{normalizeStatus(project.status)}</td>}
        <td className="px-4 py-2 border">
          <button
            onClick={() => {
  navigate(`/project/${project.id}/overview`);
}}
            className="bg-primary px-3 py-1 hover:bg-primary-dark text-sm"
          >
            🔍 Open
          </button>
        </td>
        <td className="px-4 py-2 border text-center">
  {canManageProjectActivation  ? (
    normalizeStatus(project.status) !== "active" ? (
      <button
        onClick={() => handleActivateProject(project.id)}
        className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600 text-sm"
      >
        🔄 Activate
      </button>
    ) : (
      <button
        onClick={() => openDeactivateModal(project.id)}
        className="bg-yellow-500 text-white px-3 py-1 rounded hover:bg-yellow-600 text-sm"
      >
        ⚙️ Deactivate
      </button>
    )
  ) : (
    <button
      onClick={() => openDeactivateModal(project.id)}
      className="bg-yellow-500 text-white px-3 py-1 rounded hover:bg-yellow-600 text-sm"
    >
      ⚙️ Deactivate
    </button>
  )}
</td>
 {canDeleteProjects  && (
          <td className="px-4 py-2 border text-center">
            <button
              onClick={() => openDeleteModal(project.id)}
              className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 text-sm"
            >
              🗑 Delete
            </button>
          </td>
        )}

      </tr>
    ))}
  </tbody>
</table>

      </div>

      {/* Deactivate Modal */}
      {showDeactivateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-neutral-800 p-6 rounded-lg shadow-md w-1/3">
            <h3 className="text-xl font-semibold text-primary-dark dark:text-primary-light">
              Are you sure you want to deactivate this project?
            </h3>
            <p className="text-sm text-gray-700 dark:text-gray-300 mt-2">
              This will mark the project as inactive, and it will not be displayed to users.
            </p>
            <div className="mt-4 flex justify-between">
              <button
                onClick={handleDeactivateProject}
                className="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600"
              >
                Yes, deactivate
              </button>
              <button
                onClick={closeDeactivateModal}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>

    {showDeleteModal && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-neutral-800 p-6 rounded-lg shadow-md w-1/3">
      <h3 className="text-xl font-semibold text-red-700 dark:text-red-400">
        Are you sure you want to delete this project?
      </h3>
      <p className="text-sm text-gray-700 dark:text-gray-300 mt-2">
        This action will permanently delete the project and related records.
      </p>
      <div className="mt-4 flex justify-between">
        <button
          onClick={handleDeleteProject}
          className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
        >
          Yes, delete
        </button>
        <button
          onClick={closeDeleteModal}
          className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
)}


      </div>

      <div>
        {canSeeUserActivity  && (
          <>
            <h3 className="text-3xl font-bold text-primary-light dark:text-primary-dark mb-4">
              📈 {t("dashboardkey.user_activity")}
            </h3>
            {consent?.functional ? (
              <div className="w-full h-72 bg-background-light dark:bg-neutral-900 border border-gray-200 dark:border-neutral-700 rounded shadow p-4">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={[
                      { month: "Jan", users: 100 },
                      { month: "Feb", users: 180 },
                      { month: "Mar", users: 250 },
                      { month: "Apr", users: 300 },
                      { month: "May", users: 220 },
                    ]}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#444" : "#ccc"} />
                    <XAxis dataKey="month" stroke={isDark ? "#ccc" : "#333"} />
                    <YAxis stroke={isDark ? "#ccc" : "#333"} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: isDark ? "#1e1e1e" : "#fff",
                        borderColor: "#d6c176",
                      }}
                      labelStyle={{ color: isDark ? "#fff" : "#000" }}
                      itemStyle={{ color: isDark ? "#d6c176" : "#444" }}
                    />
                    <Line
                      type="monotone"
                      dataKey="users"
                      stroke="#d6c176"
                      strokeWidth={3}
                      dot={{ fill: isDark ? "#d6c176" : "#444" }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="text-sm italic text-gray-500">
                {t("cookie.declined_notice") || "Please accept cookies to view analytics."}
              </p>
            )}
          </>
        )}
      </div>
      {showMeasure && (
  <MeasureApp onClose={() => setShowMeasure(false)} />
)}
    </div>
  );
};

export default Dashboard;